import React, { useContext, useEffect, useState } from 'react';
import { Button, Grid, Modal, NativeSelect, Select } from '@material-ui/core';

import { fbmApi as api } from 'api/fbm';
import { packagingApi as packageApi } from 'api/packaging';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications } from 'shared';
import dayjs from 'utils/dayjsConfig';

function ViewItemsTable({ fbmAccount, onRefresh, parentRow, setExpandedRow }) {
    const [poID, setPOID] = useState(parentRow.original.id);
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [addItemModalOpen, setAddItemModalOpen] = useState(false);
    const [packagingOptions, setPackagingOptions] = useState([]);

    const getPackagingOptions = async () => {
        const itemMasterRes = await api.getPackaging(fbmAccount.fbmAccountName);
        if (itemMasterRes.status === false) {
        } else {
            const items = (itemMasterRes.data?.rows ?? []).map((item) => ({
                text: `SKU ${item.model.materialNumber}: ${item.model.description}`,
                value: item.model.id,
            }));
            setPackagingOptions(items);
        }
    };

    const columns = [
        { accessor: 'vendorSku', Header: 'Item SKU' },
        { accessor: 'description', Header: 'Description' },
        {
            accessor: 'numPallets',
            Header: 'Num of Pallets',
            editable: true,
            editProps: { type: 'input', inputType: 'number' },
        },
        {
            accessor: 'unitsPerCase',
            Header: 'Units Per Case',
            editable: true,
            editProps: { type: 'input', inputType: 'number' },
        },
        { accessor: 'unitsPerCaseUOM', Header: 'Unit of Measure' },
        {
            accessor: 'qty',
            Header: 'Num of Cases',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'numOfUnits',
            Header: 'Num of Units',
        },
        {
            accessor: 'partnerLotNumber',
            Header: 'Partner Lot  Number',
            editable: true,
            editProps: { type: 'input' },
        },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <Button
                    onClick={async () => {
                        const response = await api.removePackagingASNItem(
                            poID,
                            row.original.id,
                            fbmAccount.fbmAccountName,
                        );

                        if (response.status === false) {
                            setNotification({ text: `Can't remove item ${response.msg}`, level: 'error' });
                            return;
                        }
                        await onRefresh();
                        getItemsInTable();
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    let getItemsInTable = async () => {
        const response = await api.getPackagingASNDetails(poID, fbmAccount.fbmAccountName);

        if (response.status === false) {
            setNotification({ text: `No items found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        const poDetails = response.data.output;

        // Need to add id which will populate in saveEdit
        setRows(
            (poDetails?.items ?? []).map((row) => {
                row.id = row.poMapID;
                row.unitPrice = parseFloat(row.unitPrice).toFixed(2);
                row.numOfUnits = row.qty * row.unitsPerCase;
                return row;
            }),
        );
    };

    useEffect(() => {
        getItemsInTable();
        getPackagingOptions();
    }, []);

    return (
        <div>
            <Notifications options={notification} />
            {addItemModalOpen && (
                <AddItemModal
                    fbmAccount={fbmAccount}
                    poID={poID}
                    packagingOptions={packagingOptions}
                    closeModal={() => {
                        setAddItemModalOpen(false);
                        getItemsInTable();
                    }}
                    onRefresh={onRefresh}
                />
            )}
            <DataTable
                columns={columns}
                data={rows}
                editable
                saveEdit={async ({ field, id, value }) => {
                    const updates = { [field]: value };
                    updates.fbmAccount = fbmAccount.fbmAccountName;
                    const response = await api.modifyPackagingASNItem(poID, id, updates);
                    if (response.status === false) {
                        let message = 'Error modifying ASN';
                        if (response) {
                            message += `: ${response.msg}`;
                        }
                        setNotification({ text: message, level: 'error' });
                    } else {
                        setNotification({ text: 'Packaging ASN modified!', level: 'success' });
                    }
                    await onRefresh();
                    getItemsInTable();
                }}
            />
            <Button
                onClick={() => {
                    setAddItemModalOpen(true);
                }}
            >
                Add Item
            </Button>
        </div>
    );
}

function AddItemModal({ closeModal, fbmAccount, onRefresh, packagingOptions, poID }) {
    const [error, setError] = useState(null);

    const packagingFormFields = [
        {
            name: 'id',
            inputElement: 'autocomplete',
            label: 'Packaging',
            gridValue: 8,
            inputProps: { required: true, opts: packagingOptions },
        },
        {
            name: 'qty',
            inputElement: 'textField',
            label: 'Quantity (cases)',
            gridValue: 2,
            inputProps: { type: 'number', required: true },
        },
        {
            name: 'unitsPerCase',
            inputElement: 'textField',
            label: 'Units Per Case',
            gridValue: 3,
            inputProps: { type: 'number', required: true },
        },
        {
            name: 'numPallets',
            inputElement: 'textField',
            label: 'Number of pallets',
            gridValue: 3,
            inputProps: { type: 'number', required: true },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={packagingFormFields}
                    onSubmit={async (formData) => {
                        const items = {};
                        items.items = [formData];
                        items.fbmAccount = fbmAccount.fbmAccountName;

                        const response = await api.addItemToPackagingASN(poID, items);
                        if (response.status === false) {
                            let message = 'Error adding item';
                            if (response.msg) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                            return;
                        }

                        closeModal();
                        onRefresh();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function EditDeliveryModal({ closeModal, fbmAccount, rowData }) {
    const [error, setError] = useState(null);

    const date = dayjs(rowData.scheduledDeliveryDate).format('YYYY-MM-DD');
    const time = dayjs(rowData.scheduledDeliveryDate).format('HH:mm');

    const formFields = [
        {
            name: 'poID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'Purchase Order ID',
            gridValue: 4,
            inputProps: { readOnly: true },
        },
        {
            name: 'scheduledDeliveryDate',
            initialValue: date,
            inputElement: 'date',
            disableTextInput: true,
            label: 'Scheduled Delivery Date',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'scheduledTime',
            initialValue: time,
            inputElement: 'time',
            label: 'Delivery time',
            gridValue: 4,
            inputProps: { required: true },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const item = {};
                        item.fbmAccount = fbmAccount.fbmAccountName;
                        item.scheduledDeliveryDate = formData.scheduledDeliveryDate;
                        item.deliverTime = formData.scheduledTime;

                        const response = await api.modifyPackagingASN(formData.poID, item);
                        if (response.status === false) {
                            let message = 'Error modifying purchase order';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                        } else {
                            closeModal();
                        }
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

// The main view
function ViewPackagingASN() {
    const { fbmAccount, user } = useContext(UserContext);
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [editDeliveryModalOpen, setEditDeliveryModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});

    // Load purchase orders
    const getRows = async () => {
        const response = await api.getPackagingASNs(fbmAccount.fbmAccountName);

        if (response.status === false) {
            setNotification({ text: `Can't access ASNs: ${response.msg}`, level: 'error' });
            return;
        }

        const rows = (response.data?.rows ?? []).map((row) => ({
            ...row,
            createdAt: dayjs(row.createdAt.date).format('MM/DD/YYYY'),
            scheduledDeliveryDate: dayjs(row.scheduledDeliveryDate.date).format('MM/DD/YYYY HH:mm:ss'),
        }));

        setRows(rows);
    };

    useEffect(() => {
        if (fbmAccount.fbmAccountName) {
            getRows();
        }
    }, [fbmAccount]);

    const closeDeliveryEditModal = () => {
        setEditDeliveryModalOpen(false);
    };

    const showDeliveryEditModal = (row, closeDeliveryEditModal) => {
        setSelectedRow(row);
        setEditDeliveryModalOpen(true);
    };

    const columns = [
        { accessor: 'id', Header: 'ASN ID' },
        { accessor: 'createdAt', Header: 'Created On' },
        { accessor: 'facilityName', Header: 'Facility' },
        { accessor: 'status', Header: 'Status' },
        {
            accessor: 'scheduledDeliveryDate',
            Header: 'Scheduled Delivery',
            sortType: 'datetimeString',
            editable: true,
            customEdit: showDeliveryEditModal,
        },
        { accessor: 'referenceNumber', Header: 'Reference Number', editable: true },
        { accessor: 'totalPallets', Header: 'Total Pallets' },
        {
            accessor: 'download',
            hiddenColumn: user.fbm,
            Header: 'Download',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                const [downloadOpt, setDownloadOpt] = useState('pdf');
                const downloadOptions = ['pdf', 'ship-list'];
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <NativeSelect
                            id={`Download_Select_${row.id}`}
                            className="input-element-select"
                            value={downloadOpt}
                            label="Download"
                            onChange={(event) => {
                                setDownloadOpt(event.target.value);
                            }}
                        >
                            {downloadOptions.map((value, i) => (
                                <option key={i} value={value}>
                                    {value}
                                </option>
                            ))}
                            ;
                        </NativeSelect>
                        <Button
                            id={`Download_${row.id}`}
                            onClick={() => {
                                if (downloadOpt === 'pdf') {
                                    packageApi.downloadPackagingPOPDF(row.original.id, fbmAccount.companyName);
                                } else if (downloadOpt === 'ship-list') {
                                    packageApi.downloadPackagingPOShipList(row.original.id);
                                } else {
                                    console.log(`Unrecognized download option ${downloadOpt}`);
                                }
                            }}
                        >
                            Download
                        </Button>
                    </div>
                );
            },
        },
        {
            accessor: 'cancel',
            Header: 'Cancel',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`Cancel_${row.id}`}
                        onClick={async () => {
                            const response = await api.cancelPackagingASN(row.original.id, fbmAccount.fbmAccountName);
                            if (response.status === false) {
                                setNotification({ text: `Failed to cancel ${response.msg}`, level: 'error' });
                                return;
                            }
                            setNotification({ text: 'ASN cancelled', level: 'success' });
                            await getRows();
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <h1>{fbmAccount.companyName} - Packaging Advance Shipping Notifications</h1>
            <Notifications options={notification} />
            <div>
                {editDeliveryModalOpen && (
                    <EditDeliveryModal
                        fbmAccount={fbmAccount}
                        rowData={selectedRow}
                        closeModal={() => {
                            setEditDeliveryModalOpen(false);
                            getRows();
                        }}
                    />
                )}
                <DataTable
                    columns={columns}
                    data={rows}
                    initialSortBy={[{ id: 'scheduledDeliveryDate', desc: true }]}
                    expandable
                    ExpansionComponent={
                        <ViewItemsTable
                            fbmAccount={fbmAccount}
                            onRefresh={async () => {
                                getRows();
                            }}
                        />
                    }
                    editable
                    saveEdit={async ({ field, id, value }) => {
                        const item = {};
                        item[field] = value;
                        item.fbmAccount = fbmAccount.fbmAccountName;
                        const response = await api.modifyPackagingASN(id, item);
                        if (response.status === false) {
                            let message = 'Error modifying purchase order';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'ASN modified!', level: 'success' });
                            await getRows();
                        }
                        return response.status;
                    }}
                />
            </div>
        </div>
    );
}

export { ViewPackagingASN };
